import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiClient } from "../../../../api";
import LoadingSpinner from "../../../LoadingSpinner/LoadingSpinner";
import { Edit2, ExternalLink, Download, Loader2 } from "lucide-react";
import { useProperty } from "../../../../Contexts/PropertyContext/PropertyContext";

const QR_TYPE_LABELS = {
    stand: { label: "Stand", className: "bg-indigo-100 text-indigo-800" },
    pvc: { label: "PVC Card", className: "bg-purple-100 text-purple-800" },
    stand_150_100: { label: "150×100mm", className: "bg-teal-100 text-teal-800" },
};

function QRTypeBadge({ type }) {
    const config = QR_TYPE_LABELS[type];
    return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${config.className}`}>
            {config.label}
        </span>
    );
}

export default function QRManagement() {
    const { selectedPropertyId } = useProperty();
    const [qrs, setQrs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedQR, setSelectedQR] = useState(null);
    const [editUrl, setEditUrl] = useState("");
    const [editUserId, setEditUserId] = useState("");
    const [teamMembers, setTeamMembers] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [downloadingId, setDownloadingId] = useState(null);

    // QR print template states
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const [selectedQRForDownload, setSelectedQRForDownload] = useState(null);
    const [downloadType, setDownloadType] = useState("stand");
    const [includePVCFrontDownload, setIncludePVCFrontDownload] = useState(false);
    const [filterType, setFilterType] = useState("");

    const fetchQRs = async () => {
        if (!selectedPropertyId) return;
        try {
            setIsLoading(true);
            const skip = (page - 1) * limit;
            const response = await apiClient.get(`/api/qrs?propertyId=${selectedPropertyId}&limit=${limit}&skip=${skip}&type=${filterType}`);
            if (response.status === 200) {
                setQrs(response.data.result.result);
                setTotal(response.data.result.totalCount || 0);
            }
        } catch (error) {
            toast.error("Failed to fetch QR links");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTeamMembers = async () => {
        if (!selectedPropertyId) return;
        try {
            const response = await apiClient.get(`/user/${selectedPropertyId}`);
            if (response.status === 200) {
                // Filter to only include active members (not invited)
                const members = response.data.result.filter(m => !m.type || m.type !== 'invited');
                setTeamMembers(members);
            }
        } catch (error) {
            console.error("Error fetching team members:", error);
        }
    };

    useEffect(() => {
        fetchQRs();
        fetchTeamMembers();
    }, [selectedPropertyId, page, limit, filterType]);

    const handleEdit = (qr) => {
        setSelectedQR(qr);
        setEditUrl(qr.redirectUrl || "");
        setEditUserId(qr.userId || "");
        setIsEditModalOpen(true);
    };

    const handleUpdate = async () => {
        try {
            const response = await apiClient.patch(`/api/qrs/${selectedQR._id}`, {
                userId: editUserId || null,
            });
            if (response.status === 200) {
                toast.success("QR link updated successfully");
                setIsEditModalOpen(false);
                fetchQRs();
            }
        } catch (error) {
            toast.error("Update failed");
        }
    };

    const handleDownload = async (qr, type = "stand") => {
        try {
            setDownloadingId(qr._id);
            const response = await apiClient.get(`/api/qrs/${qr._id}/download?type=${type}`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            let filename = `QR_${qr.shortCode}_Stand_5x6_inch.png`;
            if (type === "pvc") {
                filename = `QR_${qr.shortCode}_PVC_Card_Back_54x85.5mm.png`;
            } else if (type === "stand_150_100") {
                filename = `QR_${qr.shortCode}_Stand_150x100mm.png`;
            }
            link.download = filename;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            if (type === "pvc" && includePVCFrontDownload) {
                const frontResponse = await apiClient.get("/api/qrs/pvc-front", { responseType: 'blob' });
                const frontUrl = window.URL.createObjectURL(new Blob([frontResponse.data]));
                const frontLink = document.createElement('a');
                frontLink.href = frontUrl;
                frontLink.download = "PVC_Front_Card.png";
                document.body.appendChild(frontLink);
                frontLink.click();
                document.body.removeChild(frontLink);
                window.URL.revokeObjectURL(frontUrl);
            }

            toast.success(
                <div>
                    <p className="font-bold">QR Code downloaded successfully</p>
                    <p className="text-[10px] mt-1 opacity-90">If file is not downloaded please allow multi file download in chrome tab</p>
                </div>
            );
        } catch (error) {
            console.error("Error downloading QR:", error);
            toast.error("Failed to download QR code");
        } finally {
            setDownloadingId(null);
        }
    };

    return (
        <div className="bg-gray-100 p-4 sm:p-6 lg:p-8 xl:p-12 min-h-screen">
            <div className="sm:flex sm:items-center sm:justify-between">
                <div className="sm:flex-auto">
                    <h1 className="text-xl font-semibold text-gray-900">QR Management</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Manage your QR codes. Link them to specific team members for individual employee reviews or set custom redirect destinations.
                    </p>
                </div>
            </div>

            <div className="mt-8 flex flex-col">
                <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-300">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Sr No</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">QR ID</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">ShortCode</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Linked Member</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Scans</th>
                                        <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">Actions</th>
                                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Redirect Link</th>
                                        <th className="px-3 py-2 text-left text-sm font-semibold text-gray-900">
                                            <div className="flex flex-col gap-1">
                                                <span>QR Type</span>
                                                <select
                                                    value={filterType}
                                                    onChange={(e) => {
                                                        setFilterType(e.target.value);
                                                        setPage(1);
                                                    }}
                                                    className="block w-full max-w-[125px] rounded-md border-gray-300 py-1 pl-2 pr-6 text-xs font-semibold focus:border-indigo-500 focus:ring-indigo-500 bg-white shadow-sm border text-gray-700 cursor-pointer"
                                                >
                                                    <option value="">All Types</option>
                                                    <option value="stand">Stand</option>
                                                    <option value="pvc">PVC Card</option>
                                                    <option value="stand_150_100">150×100mm</option>
                                                </select>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan="7" className="py-10 flex justify-center"><LoadingSpinner /></td>
                                        </tr>
                                    ) : qrs.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="py-10 text-center text-gray-500">No QR codes found for this property.</td>
                                        </tr>
                                    ) : (
                                        qrs.map((qr, index) => (
                                            <tr key={qr._id}>
                                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                                    {(page - 1) * limit + index + 1}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-indigo-600 font-mono font-bold">
                                                    <a href={`https://${qr.fullQRLink}`} target="_blank" rel="noreferrer" className="flex items-center hover:underline" title={qr.fullQRLink}>
                                                        {qr._id}
                                                        <ExternalLink className="w-3 h-3 ml-1" />
                                                    </a>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm font-bold text-indigo-700 font-mono">
                                                    {qr.shortCode}
                                                </td>
                                                <td className="px-3 py-4 text-sm">
                                                    {qr.userName ? (
                                                        <div className="flex flex-col">
                                                            <span className="font-medium text-gray-900">{qr.userName}</span>
                                                            <span className="text-xs text-gray-500">{qr.userEmail}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                                                            Generic
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-4 text-sm text-gray-500 font-medium">
                                                    {qr.numberOfTimeScanned || 0}
                                                </td>
                                                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                                    <div className="flex items-center justify-end gap-4">
                                                        <button
                                                            onClick={() => {
                                                                if (qr.generatedType === "pvc") {
                                                                    setSelectedQRForDownload(qr);
                                                                    setDownloadType("pvc");
                                                                    setIncludePVCFrontDownload(false);
                                                                    setIsDownloadModalOpen(true);
                                                                } else {
                                                                    handleDownload(qr, qr.generatedType);
                                                                }
                                                            }}
                                                            disabled={downloadingId === qr._id}
                                                            className={`transition-all transform hover:scale-110 ${downloadingId === qr._id ? "text-indigo-400" : "text-gray-400 hover:text-indigo-600"
                                                                }`}
                                                            title="Download QR"
                                                        >
                                                            {downloadingId === qr._id ? (
                                                                <Loader2 className="w-5 h-5 animate-spin" />
                                                            ) : (
                                                                <Download className="w-5 h-5" />
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={() => handleEdit(qr)}
                                                            className="text-indigo-600 hover:text-indigo-900 flex items-center font-semibold group transition-all"
                                                        >
                                                            <Edit2 className="w-4 h-4 mr-1 group-hover:scale-110 transition-transform" /> Edit
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-4 text-sm max-w-[200px]">
                                                    <a
                                                        href={qr.effectiveRedirectUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-indigo-600 hover:underline flex items-center"
                                                        title={qr.effectiveRedirectUrl}
                                                    >
                                                        <span className="truncate">
                                                            {qr.effectiveRedirectUrl}
                                                        </span>
                                                        <ExternalLink className="w-3 h-3 ml-1 flex-shrink-0" />
                                                    </a>
                                                </td>
                                                <td className="px-3 py-4 text-sm">
                                                    <QRTypeBadge type={qr.generatedType} />
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pagination */}
            {total > 0 && (
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="text-sm text-gray-600">
                        Showing <span className="font-semibold">{(page - 1) * limit + 1}</span> to{" "}
                        <span className="font-semibold">{Math.min(page * limit, total)}</span> of{" "}
                        <span className="font-semibold">{total}</span> QR codes
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Rows per page</label>
                            <select
                                value={limit}
                                onChange={(e) => {
                                    setLimit(Number(e.target.value));
                                    setPage(1);
                                }}
                                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-indigo-500 focus:border-indigo-500 min-w-[70px] bg-white cursor-pointer"
                            >
                                {[10, 20, 50, 100].map((n) => (
                                    <option key={n} value={n}>
                                        {n}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Previous
                            </button>
                            <div className="text-sm font-medium text-gray-700 px-2">
                                Page {page} of {Math.ceil(total / limit)}
                            </div>
                            <button
                                onClick={() => setPage((p) => Math.min(Math.ceil(total / limit), p + 1))}
                                disabled={page >= Math.ceil(total / limit)}
                                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isEditModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-200">
                        <h2 className="text-lg font-bold text-gray-900 mb-2">Edit QR Link</h2>
                        <p className="text-sm text-gray-500 mb-4">Link this QR to a team member or set a custom redirect destination.</p>

                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Link to Member (Optional)</label>
                                <select
                                    value={editUserId}
                                    onChange={(e) => setEditUserId(e.target.value)}
                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-3 border transition-all"
                                >
                                    <option value="">None (Generic QR)</option>
                                    {teamMembers.map((member) => (
                                        <option key={member._id} value={member._id}>
                                            {member.name || (member.firstName ? `${member.firstName} ${member.lastName || ""}`.trim() : member.email)} ({member.email})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Custom Redirect URL</label>
                                <input
                                    type="text"
                                    value={editUrl}
                                    disabled
                                    className="block w-full rounded-lg border-gray-300 bg-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-3 border transition-all cursor-not-allowed text-gray-500"
                                    placeholder="https://maps.google.com/..."
                                />
                                <p className="mt-1 text-[10px] text-gray-400 italic">Editing the redirect link is disabled.</p>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setIsEditModalOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdate}
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-sm transition-colors"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Layout Selection Modal for Individual QR Download */}
            {isDownloadModalOpen && selectedQRForDownload && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={() => setIsDownloadModalOpen(false)}>
                    <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-200" onClick={(e) => e.stopPropagation()}>
                        <h2 className="text-lg font-bold text-gray-900 mb-2">Download PVC Card</h2>
                        <p className="text-sm text-gray-500 mb-4">
                            You are downloading the PVC Card Back for QR <strong>{selectedQRForDownload.shortCode}</strong>.
                        </p>

                        <label className="flex items-start gap-3 rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3 mb-6 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={includePVCFrontDownload}
                                onChange={(e) => setIncludePVCFrontDownload(e.target.checked)}
                                className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            />
                            <span className="flex flex-col">
                                <span className="text-sm font-medium text-indigo-900">Also download PVC Front Card</span>
                                <span className="text-xs text-indigo-700 mt-0.5">
                                    Downloads the shared `PVC_Front_Card.png` alongside the unique QR-specific PVC back file.
                                </span>
                            </span>
                        </label>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setIsDownloadModalOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setIsDownloadModalOpen(false);
                                    handleDownload(selectedQRForDownload, "pvc");
                                }}
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-sm transition-colors flex items-center"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Download
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
